/**
 * FireClientFeedback - 피드백 Firestore CRUD 서비스
 *
 * feedbacks 컬렉션에 대한 생성, 조회, 업데이트, 메시지 추가 기능.
 * 스크린샷 Storage 업로드 포함.
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, limit, onSnapshot, Unsubscribe, Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, getStorage } from 'firebase/storage';

import type { FireClient } from './FireClient';
import {
  Feedback, FeedbackMessage, FeedbackStatus, FeedbackPriority,
  FeedbackEnvironment, FeedbackDiagnostics, FEEDBACKS_COL,
  DraftImage, draftImagesPath,
} from '../models/Feedback';
import { Uid } from '../models/User';
import { Util } from './Utils/Util';

export class FireClientFeedback {
  private fc: FireClient;

  constructor(fireClient: FireClient) {
    this.fc = fireClient;
  }

  // ── 피드백 제출 ──
  async submitFeedback(feedback: Feedback): Promise<string> {
    const feedbackData = {
      ...Util.pojo(feedback),
      createdAt: Timestamp.fromDate(feedback.createdAt),
      updatedAt: Timestamp.fromDate(feedback.updatedAt),
      messages: feedback.messages.map(m => ({
        ...m,
        createdAt: Timestamp.fromDate(m.createdAt),
      })),
      // screenshots에 있는 날짜 객체를 Timestamp로 변환
      screenshots: feedback.screenshots.map(s => ({
        ...s,
        capturedAt: Timestamp.fromDate(s.capturedAt),
      })),
    };
    await setDoc(doc(this.fc.store, FEEDBACKS_COL, feedback.id), feedbackData);
    return feedback.id;
  }

  // ── 사용자 피드백 목록 ──
  async getUserFeedbacks(userId: string): Promise<Feedback[]> {
    const q = query(
      collection(this.fc.store, FEEDBACKS_COL),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => this.docToFeedback(d));
  }

  // ── 사용자 피드백 실시간 구독 ──
  subscribeUserFeedbacks(userId: string, handler: (feedbacks: Feedback[]) => void): Unsubscribe {
    const q = query(
      collection(this.fc.store, FEEDBACKS_COL),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50),
    );
    return onSnapshot(q, snap => {
      handler(snap.docs.map(d => this.docToFeedback(d)));
    });
  }

  // ── 단건 조회 ──
  async getFeedbackById(id: string): Promise<Feedback | null> {
    const snap = await getDoc(doc(this.fc.store, FEEDBACKS_COL, id));
    if (!snap.exists()) return null;
    return this.docToFeedback(snap);
  }

  // ── 단건 실시간 구독 ──
  subscribeFeedback(id: string, handler: (feedback: Feedback | null) => void): Unsubscribe {
    return onSnapshot(doc(this.fc.store, FEEDBACKS_COL, id), snap => {
      if (!snap.exists()) {
        handler(null);
        return;
      }
      handler(this.docToFeedback(snap));
    });
  }

  // ── 상태 업데이트 ──
  async updateFeedbackStatus(id: string, status: FeedbackStatus): Promise<void> {
    await updateDoc(doc(this.fc.store, FEEDBACKS_COL, id), {
      status,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  // ── 우선순위 변경 ──
  async updateFeedbackPriority(id: string, priority: FeedbackPriority): Promise<void> {
    await updateDoc(doc(this.fc.store, FEEDBACKS_COL, id), {
      priority,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  // ── 관리자 메모 업데이트 ──
  async updateAdminNote(id: string, adminNote: string): Promise<void> {
    await updateDoc(doc(this.fc.store, FEEDBACKS_COL, id), {
      adminNote,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  // ── 메시지(답변/질문) 추가 ──
  async addMessage(feedbackId: string, message: Omit<FeedbackMessage, 'id' | 'createdAt'>): Promise<void> {
    const feedback = await this.getFeedbackById(feedbackId);
    if (!feedback) throw new Error('Feedback not found');

    const newMsg: FeedbackMessage = {
      ...message,
      id: Uid.get(),
      createdAt: new Date(),
    };

    const messages = [...feedback.messages, newMsg];
    await updateDoc(doc(this.fc.store, FEEDBACKS_COL, feedbackId), {
      messages: messages.map(m => ({
        ...m,
        createdAt: Timestamp.fromDate(m.createdAt),
      })),
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  // ── 관리자용: 전체 피드백 목록 ──
  async getAllFeedbacks(): Promise<Feedback[]> {
    const q = query(
      collection(this.fc.store, FEEDBACKS_COL),
      orderBy('createdAt', 'desc'),
      limit(200),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => this.docToFeedback(d));
  }

  // ── 관리자용: 전체 피드백 실시간 구독 ──
  subscribeAllFeedbacks(handler: (feedbacks: Feedback[]) => void): Unsubscribe {
    const q = query(
      collection(this.fc.store, FEEDBACKS_COL),
      orderBy('createdAt', 'desc'),
      limit(200),
    );
    return onSnapshot(q, snap => {
      handler(snap.docs.map(d => this.docToFeedback(d)));
    });
  }

  // ── 스크린샷 업로드 ──
  async uploadScreenshot(blob: Blob, feedbackId: string): Promise<string> {
    const ext = blob.type === 'image/png' ? 'png' : 'webp';
    const fileName = `${Uid.get()}.${ext}`;
    const storagePath = `feedbacks/${feedbackId}/${fileName}`;
    const storageRef = ref(getStorage(), storagePath);
    const uploadTask = uploadBytesResumable(storageRef, blob, {
      contentType: blob.type,
    });

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        () => { /* progress (미사용) */ },
        (error) => reject(error),
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        },
      );
    });
  }

  // ── 피드백 삭제 (본인 피드백, 주로 임시저장 정리용) ──
  async deleteFeedback(id: string): Promise<void> {
    await deleteDoc(doc(this.fc.store, FEEDBACKS_COL, id));
  }

  // ══════════════════════════════════════════
  // Draft Image 관리 (경량 스크린샷 임시 보관)
  // ══════════════════════════════════════════

  // ── Draft 이미지 저장 (feedbacks/{userId}/draftImages/{id}) ──
  async saveDraftImage(draftImage: DraftImage): Promise<string> {
    const data = {
      ...draftImage,
      capturedAt: Timestamp.fromDate(draftImage.capturedAt),
    };
    await setDoc(doc(this.fc.store, draftImagesPath(draftImage.userId), draftImage.id), data);
    return draftImage.id;
  }

  // ── Draft 이미지 실시간 구독 (feedbacks/{userId}/draftImages) ──
  subscribeDraftImages(userId: string, handler: (images: DraftImage[]) => void): Unsubscribe {
    const q = query(
      collection(this.fc.store, draftImagesPath(userId)),
      orderBy('capturedAt', 'desc'),
      limit(50),
    );
    return onSnapshot(q, snap => {
      handler(snap.docs.map(d => this.docToDraftImage(d)));
    });
  }

  // ── Draft 이미지 단건 삭제 ──
  async deleteDraftImage(userId: string, id: string): Promise<void> {
    await deleteDoc(doc(this.fc.store, draftImagesPath(userId), id));
  }

  // ── Draft 이미지 일괄 삭제 (피드백 제출 시 첨부된 이미지 정리) ──
  async deleteDraftImages(userId: string, ids: string[]): Promise<void> {
    await Promise.all(ids.map(id => this.deleteDraftImage(userId, id)));
  }

  // ── Draft 이미지용 스크린샷 업로드 (feedbacks/drafts/ 경로) ──
  async uploadDraftScreenshot(blob: Blob, draftImageId: string): Promise<string> {
    const ext = blob.type === 'image/png' ? 'png' : 'webp';
    const fileName = `${draftImageId}.${ext}`;
    const storagePath = `feedbacks/drafts/${draftImageId}/${fileName}`;
    const storageRef = ref(getStorage(), storagePath);
    const uploadTask = uploadBytesResumable(storageRef, blob, {
      contentType: blob.type,
    });

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        () => {},
        (error) => reject(error),
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        },
      );
    });
  }

  // ── Firestore Doc → DraftImage 변환 ──
  private docToDraftImage(docSnap: any): DraftImage {
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      capturedAt: data.capturedAt?.toDate?.() ?? new Date(data.capturedAt),
    } as DraftImage;
  }

  // ── Firestore Doc → Feedback 변환 ──
  private docToFeedback(docSnap: any): Feedback {
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      createdAt: data.createdAt?.toDate?.() ?? new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate?.() ?? new Date(data.updatedAt),
      messages: (data.messages || []).map((m: any) => ({
        ...m,
        createdAt: m.createdAt?.toDate?.() ?? new Date(m.createdAt),
      })),
    } as Feedback;
  }
}
